class EsimCompatibilityChecker extends HTMLElement {
  static indexes = new Map();
  static maxConcurrentRequests = 3;
  static maxFetchAttempts = 3;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('[data-esim-dialog]');
    this.input = this.querySelector('[data-esim-search]');
    this.clearButton = this.querySelector('[data-esim-clear]');
    this.results = this.querySelector('[data-esim-results]');
    this.count = this.querySelector('[data-esim-device-count]');
    this.titleElement = this.querySelector('[data-esim-title]');
    this.introElement = this.querySelector('[data-esim-intro]');
    this.appleWarning = this.querySelector('[data-esim-apple-warning]');
    this.eidTemplate = this.querySelector('[data-esim-eid-template]');
    this.guidanceDisclosure = this.querySelector('[data-esim-guidance-disclosure]');
    this.pageLink = this.querySelector('[data-esim-page-link]');
    this.filterGroup = this.querySelector('[data-esim-device-filters]');
    this.filterButtons = [...this.querySelectorAll('[data-esim-device-category]')];
    this.hiddenCategories = new Set(
      String(this.dataset.hiddenDeviceCategories || '')
        .split(',')
        .map((category) => category.trim())
        .filter(Boolean),
    );
    this.brandFilter = this.querySelector('[data-esim-brand-filter]');
    this.brandTrigger = this.querySelector('[data-esim-brand-trigger]');
    this.brandValue = this.querySelector('[data-esim-brand-value]');
    this.brandList = this.querySelector('[data-esim-brand-options]');
    this.labels = this.dataset;
    this.showCompatibilityNotes = this.dataset.showCompatibilityNotes !== 'false';
    this.searchResults = [];
    this.expandedBrand = null;
    this.activeCategory = this.filterButtons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.esimDeviceCategory || 'all';
    this.selectedBrand = '';
    this.configuredBrandOptions = this.parseBrandOptions(this.brandFilter?.dataset.brandOptions);
    this.configuredNamedBrandKeys = new Set(
      this.configuredBrandOptions.filter((option) => !option.isOther).map((option) => option.value),
    );
    this.brandOptionLabels = new Map();
    this.brandOptionsSignature = null;
    this.handleDocumentClick = (event) => {
      if (this.brandFilter && !this.brandFilter.contains(event.target)) this.closeBrandMenu();
    };
    this.updatePageLinkVisibility();
    this.bindEvents();
    this.initializeIndex();
  }

  disconnectedCallback() {
    document.removeEventListener?.('click', this.handleDocumentClick);
    this.initialized = false;
  }

  updatePageLinkVisibility() {
    if (!this.pageLink) return;
    try {
      const target = new URL(this.pageLink.href, window.location.origin);
      const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
      const targetPath = target.pathname.replace(/\/+$/, '') || '/';
      this.pageLink.hidden = target.origin === window.location.origin && targetPath === currentPath;
    } catch {
      this.pageLink.hidden = false;
    }
  }

  bindEvents() {
    this.input?.addEventListener('input', () => this.onInput());
    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';
      this.input.focus();
      this.onInput();
    });
    this.results?.addEventListener('click', (event) => this.onResultsClick(event));
    this.filterGroup?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-esim-device-category]');
      if (!button || !this.filterGroup.contains(button)) return;
      this.setActiveCategory(button.dataset.esimDeviceCategory);
    });
    this.brandTrigger?.addEventListener('click', () => {
      const shouldOpen = this.brandTrigger.getAttribute('aria-expanded') !== 'true';
      this.toggleBrandMenu(shouldOpen, { focusOption: shouldOpen });
    });
    this.brandTrigger?.addEventListener('keydown', (event) => this.onBrandTriggerKeydown(event));
    this.brandList?.addEventListener('click', (event) => {
      const option = event.target.closest('[data-esim-brand-option]');
      if (!option || !this.brandList.contains(option)) return;
      this.selectBrand(option.dataset.value || '');
      this.brandTrigger?.focus();
    });
    this.brandList?.addEventListener('keydown', (event) => this.onBrandListKeydown(event));
    if (this.brandFilter) document.addEventListener('click', this.handleDocumentClick);
    this.dialog?.querySelectorAll('[data-modal-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });
    this.dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dialog.close();
    });
    this.dialog?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.dialog.close();
    });
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog?.addEventListener('close', () => {
      if (!document.querySelector('[data-esim-dialog][open]')) document.body.classList.remove('esim-compatibility--locked');
      const returnFocus = this.dialog.__returnFocus;
      this.dialog.__returnFocus = null;
      if (returnFocus?.isConnected) returnFocus.focus();
    });
  }

  open(trigger = null) {
    if (!this.dialog || this.dialog.open) return;
    this.input.value = '';
    this.clearButton.hidden = true;
    this.expandedBrand = null;
    this.activeCategory = 'all';
    this.selectedBrand = '';
    this.closeBrandMenu();
    this.updateBrandSelection();
    this.updateCategoryButtons();
    if (this.guidanceDisclosure) this.guidanceDisclosure.open = false;
    if (this.titleElement) this.titleElement.textContent = trigger?.dataset.esimHeading || this.dataset.defaultHeading;
    if (this.introElement) this.introElement.textContent = trigger?.dataset.esimIntro || this.dataset.defaultIntro;
    this.dialog.__returnFocus = trigger;
    document.body.classList.add('esim-compatibility--locked');
    this.dialog.showModal();
    this.render();
    window.requestAnimationFrame(() => this.input?.focus());
  }

  get query() {
    return this.normalize(this.input?.value || '');
  }

  normalize(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  compact(value) {
    return this.normalize(value).replace(/\s/g, '');
  }

  async loadIndex() {
    const key = `${window.location.pathname}:${this.dataset.sectionId}`;
    if (!EsimCompatibilityChecker.indexes.has(key)) {
      const request = this.fetchIndex().catch((error) => {
        EsimCompatibilityChecker.indexes.delete(key);
        throw error;
      });
      EsimCompatibilityChecker.indexes.set(key, request);
    }
    const index = await EsimCompatibilityChecker.indexes.get(key);
    this.index = index.devices;
    this.totalDevices = index.total;
    return this.index;
  }

  async initializeIndex({ force = false, restoreFocus = false } = {}) {
    const key = `${window.location.pathname}:${this.dataset.sectionId}`;
    if (force) EsimCompatibilityChecker.indexes.delete(key);
    this.isLoading = true;
    this.loadError = null;
    this.index = null;
    this.render();

    try {
      await this.loadIndex();
      this.isLoading = false;
      this.render();
      if (restoreFocus && this.dialog?.open) this.input?.focus();
    } catch (error) {
      this.isLoading = false;
      this.loadError = error;
      this.render();
      if (restoreFocus && this.dialog?.open) this.results?.querySelector('[data-esim-retry]')?.focus();
    }
  }

  async fetchIndex() {
    const firstPage = this.readPage(this.querySelector('[data-esim-device-index-page]'));
    if (!firstPage) throw new Error('The compatibility device index is unavailable.');
    this.totalPages = firstPage.pages;
    this.totalDevices = firstPage.total;
    this.pageParam = firstPage.pageParam || 'page';

    const pageNumbers = [];
    for (let page = 1; page <= firstPage.pages; page += 1) {
      if (page !== firstPage.page) pageNumbers.push(page);
    }
    const fetchedPages = await this.fetchPages(pageNumbers);
    const pages = new Map([[firstPage.page, firstPage]]);
    fetchedPages.forEach((result) => pages.set(result.page, result));

    const devices = [];
    for (let page = 1; page <= firstPage.pages; page += 1) {
      const result = pages.get(page);
      if (!result || result.pages !== firstPage.pages || result.total !== firstPage.total) {
        throw new Error(`Unable to verify compatibility device page ${page}.`);
      }
      devices.push(...result.devices);
    }

    if (devices.length !== firstPage.total) {
      throw new Error(`Expected ${firstPage.total} compatibility devices but loaded ${devices.length}.`);
    }

    return {
      devices: devices.map((device, index) => this.prepareDevice(device, index)),
      total: firstPage.total,
    };
  }

  async fetchPages(pageNumbers) {
    if (!pageNumbers.length) return [];
    const pages = new Array(pageNumbers.length);
    let nextIndex = 0;
    let failure = null;
    const workerCount = Math.min(EsimCompatibilityChecker.maxConcurrentRequests, pageNumbers.length);

    const worker = async () => {
      while (!failure && nextIndex < pageNumbers.length) {
        const resultIndex = nextIndex;
        nextIndex += 1;
        try {
          pages[resultIndex] = await this.fetchPageWithRetry(pageNumbers[resultIndex]);
        } catch (error) {
          failure = error;
        }
      }
    };

    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    if (failure) throw failure;
    return pages;
  }

  async fetchPageWithRetry(page) {
    let lastError;
    for (let attempt = 1; attempt <= EsimCompatibilityChecker.maxFetchAttempts; attempt += 1) {
      try {
        return await this.fetchPage(page);
      } catch (error) {
        lastError = error;
        if (attempt < EsimCompatibilityChecker.maxFetchAttempts) {
          await this.wait(200 * attempt);
        }
      }
    }
    throw lastError;
  }

  async fetchPage(page) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set(this.pageParam || 'page', page);
    const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Unable to load compatibility devices.');
    const markup = await response.text();
    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
    const result = this.readPage(documentFragment.querySelector('[data-esim-device-index-page]'));
    if (!result || result.page !== page) throw new Error(`Unable to verify compatibility device page ${page}.`);
    return result;
  }

  readPage(element) {
    if (!element) return null;
    try {
      const devices = JSON.parse(element.textContent || '[]');
      const page = Number(element.dataset.page || 1);
      const pages = Number(element.dataset.pages || 1);
      const total = Number(element.dataset.total || 0);
      if (!Array.isArray(devices) || !Number.isInteger(page) || page < 1) return null;
      if (!Number.isInteger(pages) || pages < 1 || !Number.isInteger(total) || total < 0) return null;
      if (page > pages) return null;
      return {
        devices,
        page,
        pages,
        total,
        pageParam: element.dataset.pageParam || 'page',
      };
    } catch (_) {
      return null;
    }
  }

  wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  prepareDevice(device, id) {
    const aliases = Array.isArray(device.aliases) ? device.aliases.filter(Boolean) : [];
    const modelIdentifiers = Array.isArray(device.modelIdentifiers) ? device.modelIdentifiers.filter(Boolean) : [];
    return {
      ...device,
      id,
      aliases,
      modelIdentifiers,
      name: device.name || '',
      brand: device.brand || '',
      notes: String(device.notes || '').trim(),
      category: this.deviceCategory(device),
      nameNormalized: this.normalize(device.name),
      brandNormalized: this.normalize(device.brand),
      aliasesNormalized: aliases.map((alias) => this.normalize(alias)),
      identifiersNormalized: modelIdentifiers.map((identifier) => this.normalize(identifier)),
      identifiersCompact: modelIdentifiers.map((identifier) => this.compact(identifier)),
    };
  }

  deviceCategory(device) {
    const explicitCategory = this.normalize(device.category || device.deviceType || device.type);
    if (/^phones?$/.test(explicitCategory)) return 'phone';
    if (/^tablets?$/.test(explicitCategory)) return 'tablet';
    if (/^laptops?$/.test(explicitCategory)) return 'laptop';
    if (/^(smart ?)?watches?$/.test(explicitCategory)) return 'watch';

    const descriptor = this.normalize([
      device.brand,
      device.name,
      device.os,
      ...(Array.isArray(device.aliases) ? device.aliases : []),
    ].filter(Boolean).join(' '));

    if (/\b(watch|smartwatch|ticwatch)(?:\b|\d)/.test(descriptor)) return 'watch';
    if (/\b(ipad|tablet|tab|matepad|mediapad|surface go|surface pro|miix|transformer)\b/.test(descriptor)) return 'tablet';
    if (this.normalize(device.os) === 'windows') return 'laptop';
    return 'phone';
  }

  updateCategoryButtons() {
    this.filterButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.esimDeviceCategory === this.activeCategory));
    });
  }

  setActiveCategory(category) {
    const allowedCategories = new Set(['all', 'phone', 'tablet', 'laptop', 'watch']);
    this.activeCategory = allowedCategories.has(category) ? category : 'all';
    this.expandedBrand = this.selectedBrand || null;
    this.closeBrandMenu();
    this.updateCategoryButtons();
    this.render();
  }

  devicesForCategory() {
    if (!this.index) return [];
    const visibleDevices = this.index.filter((device) => !this.hiddenCategories.has(device.category));
    if (!this.activeCategory || this.activeCategory === 'all') return visibleDevices;
    return visibleDevices.filter((device) => device.category === this.activeCategory);
  }

  parseBrandOptions(value) {
    const seen = new Set();
    return String(value || '')
      .split(/[\n,]+/)
      .map((option) => option.trim())
      .filter(Boolean)
      .map((label) => {
        const normalized = this.normalize(label);
        const isOther = normalized === 'other brands';
        return {
          value: isOther ? '__other__' : normalized,
          label,
          isOther,
        };
      })
      .filter((option) => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
  }

  brandOptionButtons() {
    if (!this.brandList) return [];
    if (typeof this.brandList.querySelectorAll === 'function') {
      return [...this.brandList.querySelectorAll('[data-esim-brand-option]')];
    }
    return this.brandList.options || [];
  }

  updateBrandSelection() {
    this.brandOptionButtons().forEach((option) => {
      option.setAttribute('aria-selected', String((option.dataset.value || '') === this.selectedBrand));
    });
    if (this.brandValue) {
      this.brandValue.textContent = this.brandOptionLabels.get(this.selectedBrand)
        || this.brandFilter?.dataset.allBrandsLabel
        || '';
    }
  }

  toggleBrandMenu(open, { focusOption = false } = {}) {
    if (!this.brandTrigger || !this.brandList) return;
    this.brandTrigger.setAttribute('aria-expanded', String(open));
    this.brandList.hidden = !open;
    if (!open || !focusOption) return;
    const options = this.brandOptionButtons();
    const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
    (selected || options[0])?.focus();
  }

  closeBrandMenu() {
    this.toggleBrandMenu(false);
  }

  onBrandTriggerKeydown(event) {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    this.toggleBrandMenu(true);
    const options = this.brandOptionButtons();
    const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const fallbackIndex = event.key === 'ArrowUp' ? options.length - 1 : 0;
    options[selectedIndex >= 0 ? selectedIndex : fallbackIndex]?.focus();
  }

  onBrandListKeydown(event) {
    const options = this.brandOptionButtons();
    const currentIndex = options.indexOf(document.activeElement);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, options.length - 1);
    if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - 1, 0);
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = options.length - 1;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeBrandMenu();
      this.brandTrigger?.focus();
      return;
    }
    if (event.key === 'Tab') {
      this.closeBrandMenu();
      return;
    }
    if (nextIndex === currentIndex || nextIndex < 0) return;
    event.preventDefault();
    options[nextIndex]?.focus();
  }

  selectBrand(value) {
    if (!this.brandOptionLabels.has(value)) value = '';
    this.selectedBrand = value;
    this.expandedBrand = value && value !== '__other__' ? value : null;
    this.updateBrandSelection();
    this.closeBrandMenu();
    this.render();
  }

  updateBrandOptions() {
    if (!this.brandFilter || !this.brandList || !this.index) return;
    const brands = new Map();
    this.devicesForCategory().forEach((device) => {
      if (device.brandNormalized && !brands.has(device.brandNormalized)) {
        brands.set(device.brandNormalized, device.brand || device.os || 'Other');
      }
    });
    const options = [];
    this.configuredBrandOptions.forEach((configuredOption) => {
      if (configuredOption.isOther) {
        const hasOtherBrands = [...brands.keys()]
          .some((brand) => !this.configuredNamedBrandKeys.has(brand));
        if (hasOtherBrands) {
          options.push([
            configuredOption.value,
            this.brandFilter.dataset.otherBrandsLabel || configuredOption.label,
          ]);
        }
        return;
      }
      if (brands.has(configuredOption.value)) {
        options.push([configuredOption.value, brands.get(configuredOption.value)]);
      }
    });

    const optionValues = new Set(options.map(([value]) => value));
    if (this.selectedBrand && !optionValues.has(this.selectedBrand)) {
      this.selectedBrand = '';
      this.expandedBrand = null;
    }

    const signature = `${this.activeCategory}:${options.map(([value]) => value).join('|')}`;
    if (signature !== this.brandOptionsSignature) {
      const allBrandsLabel = this.brandFilter.dataset.allBrandsLabel || '';
      this.brandOptionLabels = new Map([['', allBrandsLabel], ...options]);
      const allBrandsOption = this.createBrandOption('', allBrandsLabel);
      const brandOptions = options.map(([value, label]) => {
        return this.createBrandOption(value, label);
      });
      this.brandList.replaceChildren(allBrandsOption, ...brandOptions);
      this.brandOptionsSignature = signature;
    }
    this.updateBrandSelection();
  }

  createBrandOption(value, label) {
    const option = document.createElement('button');
    option.type = 'button';
    option.className = 'esim-compatibility__brand-option';
    option.dataset.esimBrandOption = '';
    option.dataset.value = value;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', String(value === this.selectedBrand));
    option.innerHTML = `<span>${this.escape(label)}</span><span class="esim-compatibility__brand-option-check" aria-hidden="true">${this.tickIcon()}</span>`;
    return option;
  }

  filteredDevices() {
    const devices = this.devicesForCategory();
    if (!this.selectedBrand) return devices;
    if (this.selectedBrand === '__other__') {
      return devices.filter((device) => !this.configuredNamedBrandKeys.has(device.brandNormalized));
    }
    return devices.filter((device) => device.brandNormalized === this.selectedBrand);
  }

  onInput() {
    this.clearButton.hidden = !this.input.value.trim();
    if (this.input.value.trim() && this.guidanceDisclosure) this.guidanceDisclosure.open = false;
    this.render();
  }

  onResultsClick(event) {
    const manufacturer = event.target.closest('[data-esim-manufacturer]');
    if (manufacturer) {
      const brand = manufacturer.dataset.esimManufacturer;
      this.expandedBrand = this.expandedBrand === brand ? null : brand;
      this.render();
      const replacement = [...this.results.querySelectorAll('[data-esim-manufacturer]')]
        .find((button) => button.dataset.esimManufacturer === brand);
      replacement?.focus({ preventScroll: true });
      return;
    }
    if (event.target.closest('[data-esim-reset]')) {
      this.input.value = '';
      this.activeCategory = 'all';
      this.selectedBrand = '';
      this.closeBrandMenu();
      this.updateBrandSelection();
      this.updateCategoryButtons();
      this.input.focus();
      this.onInput();
      return;
    }
    if (event.target.closest('[data-esim-retry]')) {
      this.initializeIndex({ force: true, restoreFocus: true });
    }
  }

  rank(device, query) {
    const compactQuery = this.compact(query);
    const aliasStarts = device.aliasesNormalized.some((alias) => alias.startsWith(query));
    const aliasContains = device.aliasesNormalized.some((alias) => alias.includes(query));
    const identifierExact = device.identifiersNormalized.some((identifier) => identifier === query)
      || device.identifiersCompact.some((identifier) => identifier === compactQuery);
    const identifierContains = device.identifiersNormalized.some((identifier) => identifier.includes(query))
      || device.identifiersCompact.some((identifier) => identifier.includes(compactQuery));
    const brandAndName = `${device.brandNormalized} ${device.nameNormalized}`;
    if (device.nameNormalized === query) return 1;
    if (identifierExact) return 2;
    if (device.nameNormalized.startsWith(query)) return 3;
    if (aliasStarts) return 4;
    if (brandAndName.includes(query)) return 5;
    if (device.nameNormalized.includes(query)) return 6;
    if (aliasContains) return 7;
    if (identifierContains) return 8;
    if (device.brandNormalized.includes(query)) return 9;
    return 0;
  }

  search(query) {
    return this.filteredDevices()
      .map((device) => ({ device, rank: this.rank(device, query) }))
      .filter((item) => item.rank)
      .sort((a, b) => a.rank - b.rank || a.device.name.localeCompare(b.device.name, undefined, { sensitivity: 'base' }))
      .map((item) => item.device);
  }

  render() {
    if (!this.results) return;
    if (this.isLoading || (!this.index && !this.loadError)) {
      this.renderLoading();
      return;
    }
    if (this.loadError) {
      this.renderLoadError();
      return;
    }
    this.results.setAttribute('aria-busy', 'false');
    this.updateBrandOptions();
    const devices = this.filteredDevices();
    this.updateCount(this.isLoading ? null : devices.length);
    if (!devices.length) {
      this.renderNoResults();
      return;
    }
    const query = this.query;
    if (!query) {
      this.updateAppleWarning();
      this.renderManufacturers(devices);
      return;
    }
    this.searchResults = this.search(query);
    this.updateAppleWarning(query, this.searchResults);
    if (!this.searchResults.length) {
      this.renderNoResults();
      return;
    }
    this.renderSearchResults();
  }

  renderLoading() {
    this.results.innerHTML = `<p class="esim-compatibility__empty">${this.escape(this.labels.loading)}</p>`;
    this.results.setAttribute('aria-busy', 'true');
    this.updateCount(null);
  }

  renderLoadError() {
    const state = document.createElement('div');
    state.className = 'esim-compatibility__state esim-compatibility__state--error';
    state.setAttribute('role', 'alert');
    state.innerHTML = `<h4>${this.escape(this.labels.loadErrorTitle)}</h4><p>${this.escape(this.labels.loadErrorPrompt)}</p><button class="button button--secondary" type="button" data-esim-retry>${this.escape(this.labels.retry)}</button>`;
    this.results.setAttribute('aria-busy', 'false');
    this.results.replaceChildren(state);
    this.updateCount(this.labels.devicesUnavailable);
  }

  updateCount(count) {
    if (!this.count) return;
    if (count === null) {
      this.count.textContent = this.labels.loadingDevices;
      return;
    }
    this.count.textContent = typeof count === 'number'
      ? this.format(this.labels.supportedDevicesTemplate, count)
      : count;
  }

  renderSearchResults() {
    const heading = this.heading(this.labels.matchingDevices, this.format(this.labels.resultsTemplate, this.searchResults.length));
    const list = document.createElement('div');
    list.className = 'esim-compatibility__result-list';
    list.setAttribute('role', 'list');
    this.searchResults.forEach((device) => list.append(this.createDeviceRow(device, true)));
    this.results.replaceChildren(heading, list);
  }

  renderManufacturers(devices = this.filteredDevices()) {
    const groups = new Map();
    devices.forEach((device) => {
      const key = device.brandNormalized || this.normalize(device.brand || device.os || 'Other');
      if (!groups.has(key)) groups.set(key, { label: device.brand || device.os || 'Other', devices: [] });
      groups.get(key).devices.push(device);
    });
    const list = document.createElement('div');
    list.className = 'esim-compatibility__manufacturer-list';
    [...groups.entries()]
      .sort(([, a], [, b]) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      .forEach(([key, group], groupIndex) => {
        group.devices.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        const deviceListId = `${this.results.id || 'EsimCompatibilityResults'}-group-${groupIndex}`;
        const isExpanded = this.expandedBrand === key;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'esim-compatibility__manufacturer';
        button.dataset.esimManufacturer = key;
        button.setAttribute('aria-expanded', String(isExpanded));
        button.setAttribute('aria-controls', deviceListId);
        button.innerHTML = `<strong>${this.escape(group.label)}</strong><span class="esim-compatibility__device-count">${group.devices.length}</span><span class="esim-compatibility__accordion-icon" aria-hidden="true">${this.chevronIcon()}</span>`;
        list.append(button);
        const deviceList = document.createElement('div');
        deviceList.id = deviceListId;
        deviceList.className = 'esim-compatibility__device-list';
        deviceList.setAttribute('role', 'list');
        deviceList.hidden = !isExpanded;
        group.devices.forEach((device) => deviceList.append(this.createDeviceRow(device)));
        list.append(deviceList);
      });
    this.results.replaceChildren(list);
  }

  renderNoResults() {
    this.searchResults = [];
    const state = document.createElement('div');
    const eidSlotMarkup = this.eidTemplate ? '<div data-esim-eid-slot></div>' : '';
    state.className = 'esim-compatibility__state esim-compatibility__empty';
    state.innerHTML = `<h4>${this.escape(this.labels.noResultsTitle)}</h4><p>${this.escape(this.labels.noResultsPrompt)}</p>${eidSlotMarkup}<p>${this.escape(this.labels.noResultsGuidance)}</p><button class="button button--secondary" type="button" data-esim-reset>${this.escape(this.labels.browseManufacturers)}</button>`;
    const eidSlot = state.querySelector('[data-esim-eid-slot]');
    if (eidSlot && this.eidTemplate) eidSlot.replaceWith(this.eidTemplate.content.cloneNode(true));
    this.results.replaceChildren(state);
  }

  createDeviceRow(device, searchable = false) {
    const row = document.createElement('div');
    const hasNote = this.showCompatibilityNotes !== false && Boolean(device.notes);
    row.className = searchable ? 'esim-compatibility__result' : 'esim-compatibility__device';
    if (hasNote) row.classList.add('esim-compatibility__device--with-note');
    row.setAttribute('role', 'listitem');
    const note = hasNote
      ? `<span class="esim-compatibility__device-note"><span class="esim-compatibility__device-note-label">${this.escape(this.labels.compatibilityNote)}</span><span class="esim-compatibility__device-note-copy">${this.escape(device.notes)}</span></span>`
      : '';
    row.innerHTML = `<span class="esim-compatibility__result-copy"><span class="esim-compatibility__brand">${this.escape(device.brand)}</span><span class="esim-compatibility__result-name">${this.escape(device.name)}</span>${note}</span>${searchable ? `<span class="esim-compatibility__result-status">${this.checkIcon()}<span>${this.escape(this.labels.compatible)}</span></span>` : ''}`;
    return row;
  }

  updateAppleWarning(query = '', devices = []) {
    if (!this.appleWarning) return;
    const mentionsApple = /(^|\s)(apple|iphone)(\s|$)/.test(query);
    const hasIphoneResult = devices.some((device) => device.brandNormalized === 'apple' && device.nameNormalized.includes('iphone'));
    const browsingApple = !query && this.expandedBrand === 'apple';
    this.appleWarning.hidden = !(mentionsApple || hasIphoneResult || browsingApple);
  }

  heading(title, count) {
    const heading = document.createElement('div');
    heading.className = 'esim-compatibility__results-heading';
    heading.innerHTML = `<strong>${this.escape(title)}</strong><span>${this.escape(count)}</span>`;
    return heading;
  }

  format(template, count) {
    return String(template || '').replace('[count]', count);
  }

  escape(value) {
    const element = document.createElement('div');
    element.textContent = value || '';
    return element.innerHTML;
  }

  checkIcon() {
    return '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" focusable="false"><circle cx="12" cy="12" r="8.25" stroke="currentColor" stroke-width="1.8"></circle><path d="m8.25 12.1 2.35 2.35 5.2-5.2" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"></path></svg>';
  }

  tickIcon() {
    return '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" focusable="false"><path d="m6.75 12.15 3.25 3.2 7.25-7.2" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"></path></svg>';
  }

  chevronIcon() {
    return '<svg viewBox="0 0 24 24" fill="none" focusable="false"><path d="m7.5 9.5 4.5 4.5 4.5-4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"></path></svg>';
  }
}

if (!customElements.get('esim-compatibility-checker')) {
  customElements.define('esim-compatibility-checker', EsimCompatibilityChecker);
}

const compatibilityPagePaths = new Set([
  '/pages/esim-compatibility',
  '/pages/esim-compatible-devices',
]);

function isCompatibilityPageLink(link) {
  if (!(link instanceof HTMLAnchorElement)) return false;
  if (link.hasAttribute('data-esim-page-link')) return false;

  try {
    const url = new URL(link.href, window.location.origin);
    const path = url.pathname.replace(/\/+$/, '') || '/';
    return compatibilityPagePaths.has(path);
  } catch {
    return false;
  }
}

function enhanceCompatibilityPageLinks() {
  document.querySelectorAll('a[href]').forEach((link) => {
    if (!isCompatibilityPageLink(link)) return;
    link.dataset.esimOpen = '';
    link.href = '#EsimCompatibilityDialog';
    link.removeAttribute('target');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', 'EsimCompatibilityDialog');
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enhanceCompatibilityPageLinks, { once: true });
} else {
  enhanceCompatibilityPageLinks();
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href]');
  const trigger = event.target.closest('[data-esim-open]') || (isCompatibilityPageLink(link) ? link : null);
  if (!trigger) return;
  const checker = document.querySelector('esim-compatibility-checker');
  if (!checker || typeof checker.open !== 'function') return;
  event.preventDefault();
  checker.open(trigger);
});
